import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, InlineNotification, Select, SelectItem, Tag, Toggle } from '@carbon/react';
import { api } from '../../api/client';
import { useAuth } from '../../auth/AuthContext';
import { BlockSkeleton, ErrorState, InlineError } from '../../components/states';

export type MucDoBot = 'de' | 'vua' | 'kho' | 'sieu_kho';

export type MucCuocItem = {
  blind: number;
  tat: boolean;
  soBan: number;
  mucDo?: MucDoBot;
};

export type BanItem = {
  id: string;
  blind: number;
  trangThai: string;
  soNguoi: number;
  soGhe: number;
  tat: boolean;
};

export type PokerGameManagement = {
  gid: string;
  ten: string;
  tatGiai?: boolean;
  mucCuoc?: MucCuocItem[];
  ban?: BanItem[];
};

export type PokerBanConfig = {
  vgcg_6?: { tatMuc: number[]; tatBan: string[]; mucDo?: Record<string, MucDoBot> };
  vgcg_12?: { tatMuc: number[]; tatBan: string[]; mucDo?: Record<string, MucDoBot> };
  vgcg_17?: { tatGiai?: boolean; tat?: boolean };
  ktrng_3988?: { tatMuc: number[]; tatBan: string[]; mucDo?: Record<string, MucDoBot> };
};

type PokerTablesResponse = {
  games: PokerGameManagement[];
  cauHinh: PokerBanConfig;
  ban_loi?: string;
};

function formatTien(n: number | null | undefined) {
  if (n == null || isNaN(n)) return '0';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n % 1_000 === 0 ? 0 : 1)}k`;
  return n.toLocaleString('vi-VN');
}

export function PokerTablesTab() {
  const { me } = useAuth();
  const isFounder = me?.role === 'founder';
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['poker-tables'],
    queryFn: () => api.get<PokerTablesResponse>('/api/admin/games/poker/tables'),
    refetchInterval: 3000,
  });

  const [draft, setDraft] = useState<PokerBanConfig>({});
  const [daSua, setDaSua] = useState(false);
  const [thongBao, setThongBao] = useState<string | null>(null);

  const doiMucDo = (gid: 'vgcg_6' | 'vgcg_12' | 'ktrng_3988', blind: number, mucDo: MucDoBot) => {
    setDraft((prev) => {
      const cur = prev[gid] || { tatMuc: [], tatBan: [], mucDo: {} };
      return {
        ...prev,
        [gid]: {
          ...cur,
          mucDo: {
            ...(cur.mucDo || {}),
            [String(blind)]: mucDo,
          },
        },
      };
    });
    setDaSua(true);
    setThongBao(null);
  };

  useEffect(() => {
    if (q.data && !daSua) {
      setDraft(q.data.cauHinh || {});
    }
  }, [q.data, daSua]);

  const luu = useMutation({
    mutationFn: (body: { cauHinh: PokerBanConfig }) =>
      api.put<{ ok: boolean }>('/api/admin/games/poker/tables', body),
    onSuccess: () => {
      setDaSua(false);
      setThongBao('Đã lưu cấu hình bật/tắt bàn poker thành công.');
      qc.invalidateQueries({ queryKey: ['poker-tables'] });
    },
  });

  if (q.isLoading) return <BlockSkeleton lines={8} />;
  if (q.error || !q.data) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;

  const { games, ban_loi } = q.data;

  const toggleMuc = (gid: 'vgcg_6' | 'vgcg_12' | 'ktrng_3988', blind: number, bat: boolean) => {
    setDraft((prev) => {
      const cur = prev[gid] || { tatMuc: [], tatBan: [] };
      const setTat = new Set(cur.tatMuc || []);
      if (bat) {
        setTat.delete(blind);
      } else {
        setTat.add(blind);
      }
      return {
        ...prev,
        [gid]: { ...cur, tatMuc: [...setTat] },
      };
    });
    setDaSua(true);
    setThongBao(null);
  };

  const toggleBan = (gid: 'vgcg_6' | 'vgcg_12' | 'ktrng_3988', banId: string, bat: boolean) => {
    setDraft((prev) => {
      const cur = prev[gid] || { tatMuc: [], tatBan: [] };
      const setTat = new Set(cur.tatBan || []);
      if (bat) {
        setTat.delete(banId);
      } else {
        setTat.add(banId);
      }
      return {
        ...prev,
        [gid]: { ...cur, tatBan: [...setTat] },
      };
    });
    setDaSua(true);
    setThongBao(null);
  };

  const toggleGiai = (bat: boolean) => {
    setDraft((prev) => ({
      ...prev,
      vgcg_17: { tatGiai: !bat, tat: !bat },
    }));
    setDaSua(true);
    setThongBao(null);
  };

  const laMucBiTat = (gid: 'vgcg_6' | 'vgcg_12' | 'ktrng_3988', blind: number) => {
    const cur = draft[gid]?.tatMuc || [];
    return cur.includes(blind);
  };

  const laBanBiTat = (gid: 'vgcg_6' | 'vgcg_12' | 'ktrng_3988', banId: string) => {
    const cur = draft[gid]?.tatBan || [];
    return cur.includes(banId);
  };

  const laGiaiBiTat = () => {
    return draft.vgcg_17?.tatGiai === true || draft.vgcg_17?.tat === true;
  };

  return (
    <div className="poker-tables-tab" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginTop: '1rem' }}>
      {ban_loi && (
        <InlineNotification
          kind="warning"
          lowContrast
          hideCloseButton
          title="Kết nối máy game"
          subtitle="Không thể đồng bộ trạng thái bàn từ máy game. Vui lòng thử lại sau."
        />
      )}

      {thongBao && (
        <InlineNotification
          kind="success"
          lowContrast
          title="Thành công"
          subtitle={thongBao}
          onClose={() => { setThongBao(null); return false; }}
        />
      )}

      {luu.error && <InlineError error={luu.error} />}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem', background: 'var(--cds-layer-01)', borderRadius: '4px' }}>
        <div>
          <h4 style={{ margin: 0 }}>Cấu hình Bật/Tắt Bàn & Mức cược</h4>
          <p className="muted" style={{ margin: '0.25rem 0 0' }}>
            Khi tắt mức cược hoặc bàn cụ thể: server sẽ ngừng nhận người mới, ván đang dở chạy hết rồi đóng và hoàn trả stack an toàn.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {daSua && <Tag type="warm-gray">Có thay đổi chưa lưu</Tag>}
          <Button
            size="md"
            kind="primary"
            disabled={!daSua || luu.isPending}
            onClick={() => luu.mutate({ cauHinh: draft })}
          >
            {luu.isPending ? 'Đang lưu...' : 'Lưu cấu hình bàn'}
          </Button>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
        {games.map((g) => {
          const gid = g.gid as 'vgcg_6' | 'vgcg_12' | 'vgcg_17' | 'ktrng_3988';

          if (gid === 'vgcg_17') {
            const tat = laGiaiBiTat();
            return (
              <div key={g.gid} style={{ padding: '1.25rem', border: '1px solid var(--cds-border-subtle)', borderRadius: '6px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '1.125rem' }}>{g.ten} ({g.gid})</h3>
                    <p className="muted" style={{ margin: '0.25rem 0 0' }}>Poker Giải đấu nhiều người (Sit & Go)</p>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <Tag type={tat ? 'red' : 'green'}>{tat ? 'Đang tắt' : 'Đang mở'}</Tag>
                    <Toggle
                      id={`toggle-${g.gid}`}
                      labelText=""
                      labelA="Tắt"
                      labelB="Bật"
                      toggled={!tat}
                      onToggle={(bat) => toggleGiai(bat)}
                    />
                  </div>
                </div>
              </div>
            );
          }

          return (
            <div key={g.gid} style={{ padding: '1.25rem', border: '1px solid var(--cds-border-subtle)', borderRadius: '6px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.125rem' }}>{g.ten} ({g.gid})</h3>
                  <p className="muted" style={{ margin: '0.25rem 0 0' }}>
                    {gid === 'ktrng_3988' ? 'Stud Poker bàn chung với dealer' : 'Poker bàn bài nhiều người'}
                  </p>
                </div>
              </div>

              {/* Mức cược */}
              <div style={{ marginBottom: '1.5rem' }}>
                <h5 style={{ margin: '0 0 0.75rem', fontSize: '0.875rem', color: 'var(--cds-text-secondary)' }}>
                  DANH SÁCH MỨC CƯỢC (BLIND / ANTE)
                </h5>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.75rem' }}>
                  {(g.mucCuoc || []).map((m) => {
                    const mucTat = laMucBiTat(gid, m.blind);
                    return (
                      <div
                        key={m.blind}
                        style={{
                          padding: '0.75rem 1rem',
                          background: 'var(--cds-layer-01)',
                          borderRadius: '4px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.5rem',
                          border: mucTat ? '1px dashed var(--cds-support-error)' : '1px solid var(--cds-border-subtle)',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <div style={{ fontWeight: 600 }}>{formatTien(m.blind)}</div>
                            <div className="muted" style={{ fontSize: '0.75rem' }}>{m.soBan} bàn hoạt động</div>
                          </div>
                          <Toggle
                            id={`muc-${g.gid}-${m.blind}`}
                            size="sm"
                            labelText=""
                            labelA="Tắt"
                            labelB="Bật"
                            toggled={!mucTat}
                            onToggle={(bat) => toggleMuc(gid, m.blind, bat)}
                          />
                        </div>
                        <div style={{ width: '100%' }}>
                          <Select
                            id={`muc-do-${g.gid}-${m.blind}`}
                            size="sm"
                            labelText="Mức độ bot"
                            value={draft[gid]?.mucDo?.[String(m.blind)] || m.mucDo || 'kho'}
                            disabled={!isFounder}
                            onChange={(e) => doiMucDo(gid, m.blind, e.target.value as MucDoBot)}
                          >
                            <SelectItem value="de" text="Dễ (Người mới)" />
                            <SelectItem value="vua" text="Vừa (Cân bằng)" />
                            <SelectItem value="kho" text="Khó (Tối ưu)" />
                            <SelectItem value="sieu_kho" text="Siêu khó (Bảo vệ)" />
                          </Select>
                          {!isFounder && <span className="muted" style={{ fontSize: '0.7rem' }}>Chỉ founder chỉnh</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Từng bàn chơi */}
              <div>
                <h5 style={{ margin: '0 0 0.75rem', fontSize: '0.875rem', color: 'var(--cds-text-secondary)' }}>
                  DANH SÁCH BÀN CHƠI CỤ THỂ
                </h5>
                {(!g.ban || g.ban.length === 0) ? (
                  <p className="muted">Không có bàn nào đang mở.</p>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid var(--cds-border-subtle)', textAlign: 'left' }}>
                          <th style={{ padding: '0.5rem' }}>Mã Bàn</th>
                          <th style={{ padding: '0.5rem' }}>Mức Blind</th>
                          <th style={{ padding: '0.5rem' }}>Người ngồi</th>
                          <th style={{ padding: '0.5rem' }}>Trạng thái</th>
                          <th style={{ padding: '0.5rem', textAlign: 'right' }}>Hành động</th>
                        </tr>
                      </thead>
                      <tbody>
                        {g.ban.map((b) => {
                          const banTat = laBanBiTat(gid, b.id);
                          const mucTat = laMucBiTat(gid, b.blind);
                          const dangNgung = banTat || mucTat;
                          return (
                            <tr key={b.id} style={{ borderBottom: '1px solid var(--cds-border-subtle)' }}>
                              <td style={{ padding: '0.5rem', fontFamily: 'monospace' }}>{b.id}</td>
                              <td style={{ padding: '0.5rem' }}>{formatTien(b.blind)}</td>
                              <td style={{ padding: '0.5rem' }}>{b.soNguoi} / {b.soGhe}</td>
                              <td style={{ padding: '0.5rem' }}>
                                <Tag type={dangNgung ? 'red' : b.trangThai === 'DANG_CHOI' ? 'green' : 'blue'}>
                                  {dangNgung ? 'Tạm dừng' : b.trangThai}
                                </Tag>
                              </td>
                              <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                                <Toggle
                                  id={`ban-${g.gid}-${b.id}`}
                                  size="sm"
                                  labelText=""
                                  labelA="Tắt"
                                  labelB="Bật"
                                  disabled={mucTat}
                                  toggled={!banTat && !mucTat}
                                  onToggle={(bat) => toggleBan(gid, b.id, bat)}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
